/**
 * FieldToolbar — compact bar for surface visualization controls.
 *
 * Sits between the entity dropdown toolbar and the 3D viewer area.
 * Provides: field selector, colormap picker, min/max range, contour steps,
 * wireframe toggle, LIC toggle, and swipe comparison slider.
 */

import { memo, useCallback, useState, useRef, useEffect, type CSSProperties } from "react";

export interface FieldToolbarProps {
  fieldNames: string[];
  colormaps: string[];
  activeField?: string;
  activeColormap?: string;
  range: [number, number] | null;
  contourSteps: number;
  wireframe: boolean;
  lic: boolean;
  planform?: boolean;
  hasPlanformData?: boolean;
  onFieldChange: (name: string | undefined) => void;
  onColormapChange: (name: string) => void;
  onRangeChange: (min: number, max: number) => void;
  onContourStepsChange: (steps: number) => void;
  onWireframeToggle: () => void;
  onLICToggle: () => void;
  onPlanformToggle?: () => void;
}

const BTN: CSSProperties = {
  padding: "2px 6px",
  borderRadius: 4,
  border: "1px solid var(--gray-a6)",
  fontSize: 11,
  cursor: "pointer",
  background: "var(--gray-a3)",
  color: "var(--gray-12)",
  whiteSpace: "nowrap",
  display: "inline-flex",
  alignItems: "center",
  gap: 3,
};

const BTN_ACTIVE: CSSProperties = {
  ...BTN,
  background: "var(--accent-a4)",
  border: "1px solid var(--accent-a7)",
};

const SELECT: CSSProperties = {
  padding: "2px 4px",
  border: "1px solid var(--gray-a6)",
  borderRadius: 4,
  background: "var(--color-surface, #1a1a2e)",
  color: "var(--gray-12)",
  fontSize: 11,
  maxWidth: 120,
};

const NUM_INPUT: CSSProperties = {
  ...SELECT,
  width: 56,
  textAlign: "center" as const,
  MozAppearance: "textfield" as any,
};

const WireframeIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <line x1="3" y1="12" x2="21" y2="12" />
    <line x1="12" y1="3" x2="12" y2="21" />
  </svg>
);

const LICIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 12c2-4 6-8 10-4s8 0 10-4" />
    <path d="M2 20c2-4 6-8 10-4s8 0 10-4" />
  </svg>
);

const PlanformIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 20L8 4h8l4 16H4z" />
    <line x1="4" y1="20" x2="20" y2="20" />
  </svg>
);

export const FieldToolbar = memo(function FieldToolbar({
  fieldNames,
  colormaps,
  activeField,
  activeColormap,
  range,
  contourSteps,
  wireframe,
  lic,
  planform,
  hasPlanformData,
  onFieldChange,
  onColormapChange,
  onRangeChange,
  onContourStepsChange,
  onWireframeToggle,
  onLICToggle,
  onPlanformToggle,
}: FieldToolbarProps) {
  const [localMin, setLocalMin] = useState("");
  const [localMax, setLocalMax] = useState("");
  const rangeInitRef = useRef(false);

  useEffect(() => {
    if (range) {
      setLocalMin(range[0].toPrecision(4));
      setLocalMax(range[1].toPrecision(4));
      rangeInitRef.current = true;
    }
  }, [range?.[0], range?.[1]]);

  const commitRange = useCallback(() => {
    const min = parseFloat(localMin);
    const max = parseFloat(localMax);
    if (!isNaN(min) && !isNaN(max) && min < max) {
      onRangeChange(min, max);
    }
  }, [localMin, localMax, onRangeChange]);

  if (fieldNames.length === 0) return null;

  return (
    <div className="f3v-field-toolbar">
      {/* Field selector */}
      <label className="f3v-field-label">
        Color by
        <select
          value={activeField ?? ""}
          onChange={(e) =>
            onFieldChange(e.target.value || undefined)
          }
          style={SELECT}
        >
          <option value="">None</option>
          {fieldNames.map((f) => (
            <option key={f} value={f}>{f}</option>
          ))}
        </select>
      </label>

      {activeField && (
        <>
          {colormaps.length > 0 && (
            <label className="f3v-field-label">
              Map
              <select
                value={activeColormap ?? ""}
                onChange={(e) => onColormapChange(e.target.value)}
                style={SELECT}
              >
                {colormaps.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </label>
          )}

          <label className="f3v-field-label">
            Min
            <input
              type="number"
              value={localMin}
              onChange={(e) => setLocalMin(e.target.value)}
              onBlur={commitRange}
              onKeyDown={(e) => e.key === "Enter" && commitRange()}
              style={NUM_INPUT}
            />
          </label>
          <label className="f3v-field-label">
            Max
            <input
              type="number"
              value={localMax}
              onChange={(e) => setLocalMax(e.target.value)}
              onBlur={commitRange}
              onKeyDown={(e) => e.key === "Enter" && commitRange()}
              style={NUM_INPUT}
            />
          </label>

          <label className="f3v-field-label">
            Steps
            <input
              type="number"
              min={0}
              max={100}
              value={contourSteps}
              onChange={(e) => {
                const v = parseInt(e.target.value, 10);
                if (!isNaN(v) && v >= 0) onContourStepsChange(v);
              }}
              style={{ ...NUM_INPUT, width: 40 }}
            />
          </label>
        </>
      )}

      <div className="f3v-field-separator" />

      <button
        style={wireframe ? BTN_ACTIVE : BTN}
        onClick={onWireframeToggle}
        title="Toggle wireframe"
      >
        <WireframeIcon /> Wire
      </button>

      <button
        style={lic ? BTN_ACTIVE : BTN}
        onClick={onLICToggle}
        title="Line Integral Convolution (flow lines)"
      >
        <LICIcon /> LIC
      </button>

      {hasPlanformData && onPlanformToggle && (
        <button
          style={planform ? BTN_ACTIVE : BTN}
          onClick={onPlanformToggle}
          title="Toggle planform analysis overlay"
        >
          <PlanformIcon /> Planform
        </button>
      )}

    </div>
  );
});
